import React, { useEffect } from 'react';
import { change, formValueSelector } from 'redux-form';
import { useAuth0 } from '@auth0/auth0-react';
import { axiosInstance } from './api/axiosInstance';
import { setUnauthorizedHandler, handleUnauthorized } from './api/authBridge';
import { isStandalonePublicPath } from '@utils/standalonePublicPaths';
import { auth0AuthroizationParams } from './App';
import { jwtDecode } from 'jwt-decode';
import { setUser, logout } from '@store/actions';
import { connect, useDispatch } from 'react-redux';
import { RootState } from '@store/reducers';
import { isSyntheticTest } from '@utils/platformBasedInfo';
import { disableGaForStaff } from '@utils/ga';
import { useIsInIframe, useWasOpenedByWindowOpen } from 'shared-components';
import { recordApplyEvent } from '@utils/recordEvent';

const Auth0LoggedInSetStateInner = ({
  owner_1_first,
  owner_1_last,
  owner_1_mobile,
  ownerEmail,
}: {
  owner_1_first?: string;
  owner_1_last?: string;
  owner_1_mobile?: string;
  ownerEmail?: string;
}) => {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims,
    handleRedirectCallback,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();
  const dispatch = useDispatch();
  const isOpenedInWindow = useWasOpenedByWindowOpen();
  const isOpenedInIframe = useIsInIframe();

  // When an API call returns 401, or a silent token refresh fails (expired/
  // invalid Auth0 token or refresh token), clear the stale session and re-auth
  // instead of proceeding — otherwise the app either shows a generic create
  // error or gets stuck on an uncaught "invalid refresh token".
  useEffect(() => {
    setUnauthorizedHandler(() => {
      dispatch(logout());
      // In an embedded iframe, do NOT trigger an Auth0 redirect — it breaks the
      // embedded landing (see the Apply embed incident). Clearing auth lets the
      // in-app login step take over.
      if (isOpenedInIframe) return;

      // Standalone public forms (/partners, /contact) never need a session.
      // A stale one (expired token whose refresh is rejected) is dropped and
      // the visitor stays on the form instead of being sent to Auth0.
      if (isStandalonePublicPath(window.location.pathname)) {
        delete axiosInstance.defaults.headers.common['Authorization'];
        return;
      }

      // Loop guard: if we already bounced through login very recently and STILL
      // hit an auth failure, the session can't be recovered — fully clear it
      // (Auth0 logout) and land on the first page instead of redirect-looping.
      const REAUTH_KEY = 'apply_reauth_at';
      let lastReauth = 0;
      try {
        lastReauth = Number(window.sessionStorage.getItem(REAUTH_KEY)) || 0;
      } catch {}
      if (Date.now() - lastReauth < 30000) {
        try {
          window.sessionStorage.removeItem(REAUTH_KEY);
        } catch {}
        auth0Logout({ logoutParams: { returnTo: window.location.origin } });
        return;
      }
      try {
        window.sessionStorage.setItem(REAUTH_KEY, String(Date.now()));
      } catch {}
      loginWithRedirect({
        authorizationParams: { connection: 'email' },
        appState: { returnTo: window.location.pathname + window.location.search },
      });
    });
    return () => setUnauthorizedHandler(null);
  }, [loginWithRedirect, auth0Logout, dispatch, isOpenedInIframe]);

  useEffect(() => {
    if (!isAuthenticated) {
      console.log('Not authenticated in Auth0LoggedInSetState');
      return;
    }
    (async () => {
      try {
        const token = await getAccessTokenSilently({ authorizationParams: auth0AuthroizationParams });
        const idToken = await getIdTokenClaims();
        const decodedToken = jwtDecode(token);
        // console.log('decodedToken1', decodedToken);
        // console.log('idToken1', idToken);
        const data = {
          user: {
            loggedIn: isAuthenticated,
            token,
            image: idToken?.picture || '',
            email: idToken?.email || '',
            name: idToken?.nickname ?? '',
            firstName: idToken?.given_name || '',
            lastName: idToken?.family_name || '',
            id: (decodedToken as any)?.['https://tento.co/user_id'] || null,
            expires: decodedToken && decodedToken.exp ? decodedToken.exp * 1000 : undefined,
          },
        };

        // Tento staff are ordinary humans to GA4 — a staff run through Apply
        // would file a real generate_lead / sign_up key event. The shell
        // installed the tag before anyone signed in, so switch GA off for the
        // rest of this page as soon as the id token names a staff address.
        disableGaForStaff(data?.user?.email);

        if (data?.user?.id) {
          if (!isSyntheticTest) {
            analytics.identify(data?.user?.id, {
              name:
                data.user.name && data.user.name !== ''
                  ? data.user.name
                  : owner_1_first && owner_1_last
                  ? `${owner_1_first} ${owner_1_last}`
                  : '',
              email: data.user.email,
              // snake_case on purpose: Segment passes the whole traits object
              // through to Customer.io, and every campaign/template/snippet
              // there reads first_name/last_name. camelCase traits created a
              // second, unread copy of the same data on each profile.
              first_name: data.user.firstName && data.user.firstName !== '' ? data.user.firstName : owner_1_first,
              last_name: data.user.lastName && data.user.lastName !== '' ? data.user.lastName : owner_1_last,
              // plan: "premium",
              // logins: 5
            });
            analytics.track('Apply Form - Verify Submit', {
              type: 'after page load with auth0 user - can be: just logged in/refresh/reopened after previously being logged in',
            });
            recordApplyEvent('Apply Form - Verify Submit', {
              type: 'after page load with auth0 user - can be: just logged in/refresh/reopened after previously being logged in',
            });
          }
        }

        if (token) {
          axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + token;
          dispatch(setUser(data));
          dispatch(change('application', 'owner_1_email', idToken?.email));
        }
        if (isOpenedInIframe && window && window.parent) {
          window.parent.postMessage(
            JSON.stringify({
              type: 'identify-user',
              user: {
                id: (decodedToken as any)?.['https://tento.co/user_id'] || null,
                email: idToken?.email,
                firstName: idToken?.given_name || '',
                lastName: idToken?.family_name || '',
              },
            }),
            '*'
          );
        }
        if (isOpenedInWindow && window && window.opener) {
          window.opener.postMessage(
            JSON.stringify({
              type: 'identify-user',
              user: {
                id: (decodedToken as any)?.['https://tento.co/user_id'] || null,
                email: idToken?.email,
                firstName: idToken?.given_name || '',
                lastName: idToken?.family_name || '',
              },
            }),
            '*'
          );
        }
      } catch (error) {
        // Silent token refresh failed (e.g. an invalid/expired refresh token)
        // while Auth0 still reports authenticated — re-authenticate instead of
        // leaving the user stuck with an uncaught "invalid refresh token".
        console.warn('Auth0 silent token refresh failed; re-authenticating', error);
        handleUnauthorized();
      }
    })();
  }, [isAuthenticated]);

  return null;
};

const appSelector = formValueSelector('application');
export const Auth0LoggedInSetState = connect((state: RootState) => {
  return {
    ownerEmail: appSelector(state, 'owner_1_email'),
    owner_1_first: appSelector(state, 'owner_1_first'),
    owner_1_last: appSelector(state, 'owner_1_last'),
    owner_1_mobile: appSelector(state, 'owner_1_mobile'),
  };
})(Auth0LoggedInSetStateInner);
